/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent, Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import DayjsUtils from '@date-io/dayjs';

import MaterialTable, { EditComponentProps, MTableEditRow } from 'material-table';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Grid, Link, MenuItem, Paper, Select, TablePagination, Tooltip, Typography } from '@material-ui/core';
import { Security } from '@material-ui/icons';

import ChannelListDropdown from '../channels/ChannelListDropdown';
import AiringActionMenu from './AiringActionMenu';
import { useLanguageContext } from '../languages';
import { Airing, PaginationOptions, AlternateId, USE_DRM } from '../../utils/types';
import { getLocalTimezone } from '../../utils/dateTransform';
import commonMessages from '../common/messages';
import SimpleTextInfo from '../common/SimpleTextInfo';
import theme from '../../theme';
import StatusChip, { statusChipStyles } from '../common/StatusChip';

export const Airings: FC<AiringsProps> = (props) => {
	const { airings, defaultChannelName, gameId, gameStartTime, onRowAdd, onRowDelete, paginationOptions, setAutoRefetch, sport, useDrm } = props; //onRowUpdate removed as faulty

	const { pageSize, page, handlePageSizeChange, handlePageChange, pageOptions } = paginationOptions;
	const currentPage = airings.slice(page * pageSize, page * pageSize + pageSize);

	const statusChipClasses = statusChipStyles();

	const intl = useIntl();
	const { tableLocalization } = useLanguageContext();

	const reloadAirings = (): void => handlePageSizeChange(pageSize);

	const detailPanel = (rowData: Airing): JSX.Element => {
		return (
			<Grid container direction='column' spacing={1} style={{ width: '90%', margin: theme.spacing(2) }}>
				<Typography>Alternate Ids:</Typography>
				{rowData.alternateIds?.items?.length ? (
					[...rowData.alternateIds.items]
						.sort((a, b) => a.namespace.localeCompare(b.namespace))
						.map(
							(alternateId: AlternateId): JSX.Element => (
								<SimpleTextInfo label={alternateId.namespace} value={alternateId.id.substring(alternateId.id.indexOf(':') + 1)!} />
							)
						)
				) : (
					<Typography>none</Typography>
				)}
			</Grid>
		);
	};

	const columns = [
		{
			title: 'ID',
			field: 'id',
			editable: 'never',
			render: (row: Airing): JSX.Element => (
				<Link component={RouterLink as any} to={`/airings/${row.id}`} color='secondary'>
					{row.id}
				</Link>
			)
		},
		{
			title: 'Status',
			field: 'status',
			editable: 'never',
			align: 'center',
			render: (row: Airing): JSX.Element => <StatusChip className={statusChipClasses.smallChip} status={row.status} />
		},
		{
			title: 'Channel ID',
			field: 'channelId',
			editComponent: (props: EditComponentProps<Airing>): JSX.Element => {
				const handleChannelChange = (event: ChangeEvent<HTMLInputElement>): void => {
					props.onChange(event.target.value);
				};
				return (
					<ChannelListDropdown
						channelId={props.value ?? null}
						defaultChannelName={defaultChannelName}
						hideLabel={true}
						onSelectChange={handleChannelChange}
					/>
				);
			}
		},
		{
			title: 'Channel Territory',
			field: 'channel.territory',
			editable: 'never'
		},
		{
			title: 'Business Logic',
			field: 'businessLogic',
			lookup: {
				null: 'League Default',
				Bundesliga: 'Bundesliga',
				ChampionsLeague: 'Champions League',
				PremierLeague: 'Premier League'
			}
		},
		{
			title: 'MAF Stage',
			field: 'mafStage',
			align: 'center',
			lookup: {
				null: 'Legacy',
				STAGING: 'Staging',
				PRODUCTION: 'Production'
			},
			editable: 'onAdd',
			editComponent: (props: EditComponentProps<Airing>): JSX.Element => {
				const handleMafStageChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>): void => {
					props.onChange(event.target.value);
				};
				return (
					<Select id='mafStage' name='mafStage' onChange={handleMafStageChange} fullWidth defaultValue='DEFAULT'>
						<MenuItem key='mafStageDefault' value='DEFAULT'>
							Default
						</MenuItem>
						<MenuItem key='mafStageStaging' value='STAGING'>
							Staging
						</MenuItem>
						<MenuItem key='mafStageProduction' value='PRODUCTION'>
							Production
						</MenuItem>
					</Select>
				);
			}
		},
		{
			title: `Start time (${getLocalTimezone()})`,
			field: 'startTime',
			align: 'center',
			render: (row: Airing): JSX.Element => (
				<span>
					{row.startTime && dayjs(row.startTime).isValid() ? (
						<>
							<strong>{dayjs(row.startTime).format('DD MMM YYYY')}</strong> {dayjs(row.startTime).format('HH:mm')}
						</>
					) : (
						'Unknown'
					)}
				</span>
			),
			editComponent: (props: EditComponentProps<Airing>): JSX.Element => (
				<MuiPickersUtilsProvider utils={DayjsUtils}>
					<DateTimePicker
						id='startTime'
						name='startTime'
						value={props.value}
						onChange={(date: dayjs.Dayjs | null): void => props.onChange(date?.toISOString())}
						InputProps={{
							style: { fontSize: '0.8rem' }
						}}
					/>
				</MuiPickersUtilsProvider>
			),
			initialEditValue: gameStartTime ? dayjs(gameStartTime) : dayjs()
		},
		{
			title: 'Options',
			sorting: false,
			align: 'center',
			cellStyle: {
				verticalAlign: 'top'
			},
			render: (row: Airing): JSX.Element => <AiringActionMenu airing={row} gameId={gameId} sport={sport} reloadAirings={reloadAirings} />
		}
	];

	if ([USE_DRM.YES, USE_DRM.BOTH].includes(useDrm))
		columns.splice(2, 0, {
			title: 'DRM',
			field: 'drm',
			render: (row: Airing): JSX.Element | null =>
				row.drm ? (
					<Tooltip title='DRM Protected'>
						<Security color='action' />
					</Tooltip>
				) : null,
			editable: 'never',
			align: 'center'
		});

	const components = {
		Container: (props): JSX.Element => <Paper {...props} data-testid='airings-table' />,
		Pagination: ({ style }): JSX.Element => (
			<TablePagination
				style={style}
				rowsPerPage={pageSize}
				rowsPerPageOptions={pageOptions}
				count={airings.length}
				onRowsPerPageChange={(e): void => handlePageSizeChange(Number(e.target.value))}
				page={page}
				onPageChange={(e, page): void => handlePageChange(page)}
				labelDisplayedRows={({ from, to }): ReactNode =>
					intl.formatMessage(
						{
							...commonMessages.paginationLabel
						},
						{ from, to }
					)
				}
			/>
		),
		EditRow: (props: { mode: string }): JSX.Element => {
			if (props.mode === 'add') {
				setAutoRefetch(false);
			}
			return <MTableEditRow {...props} />;
		}
	};

	const editable = {
		onRowAdd,
		onRowAddCancelled: (): void => setAutoRefetch(true),
		onRowDelete
	}; // onRowUpdate removed as faulty

	const options = { pageSize: pageSize, search: false, sorting: false, emptyRowsWhenPaging: false };

	return (
		<div style={{ maxWidth: '100%' }}>
			<MaterialTable
				columns={columns}
				components={components}
				data={currentPage}
				detailPanel={detailPanel}
				editable={editable}
				localization={tableLocalization}
				options={options}
				title='Airings'
			/>
		</div>
	);
};

type AiringsProps = {
	airings: Airing[];
	defaultChannelName: string;
	gameId: string;
	gameStartTime: string | null;
	onRowAdd?: (newData: Airing) => Promise<unknown>;
	onRowDelete?: (oldData: Airing) => Promise<unknown>;
	onRowUpdate?: (newData: Airing, oldData?: Airing) => Promise<unknown>;
	paginationOptions: PaginationOptions;
	setAutoRefetch: Dispatch<SetStateAction<boolean>>;
	sport: string | null | undefined;
	useDrm: USE_DRM;
};

export default Airings;
